import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts'
import LoadingSpinner from '../../components/LoadingSpinner'
import { useOverview } from './hooks/useOverview'
import type { BruktbilfinansieringFilters } from './hooks/useBruktbilfinansiering'
import BruktbilfinansieringInfoBox from './BruktbilfinansieringInfoBox'
import UkentligTrendChart from './UkentligTrendChart'

interface OverviewTabProps {
  bankOrgNr: string
  bankName: string
  filters: BruktbilfinansieringFilters
}

const _nb = new Intl.NumberFormat('nb-NO')
const _nbNok = new Intl.NumberFormat('nb-NO', { style: 'currency', currency: 'NOK', maximumFractionDigits: 0 })

function formatCount(v: number): string { return _nb.format(v) }
function formatNok(v: number): string { return _nbNok.format(v) }
function formatShare(v: number): string { return _nb.format(+(v * 100).toFixed(1)) + ' %' }


const COLORS = ['#1e3a8a', '#1d4ed8', '#3b82f6', '#60a5fa', '#93c5fd', '#bfdbfe', '#dbeafe']

export default function OverviewTab({ bankOrgNr, bankName, filters }: OverviewTabProps) {
  const { t } = useTranslation('bbf')
  const { data, loading, error } = useOverview(bankOrgNr, filters)
  const [pieMode, setPieMode] = useState<'antall' | 'volum'>('antall')

  const trend = data?.weekly_trend ?? []

  const pieSlices = data?.bank_share_pie ?? []
  const pieData = pieSlices.map(s => ({
    name: s.bank_name,
    value: pieMode === 'volum' ? (s.total_nok ?? 0) : s.count,
  }))
  const pieTotal = pieData.reduce((sum, d) => sum + d.value, 0)

  if (loading) return <LoadingSpinner />

  if (error) return (
    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
      <p className="text-sm text-red-800 dark:text-red-200"><strong>{t('shared.errorLabel')}</strong> {error}</p>
    </div>
  )

  const kpis = data?.kpis
  const isEmpty = kpis && kpis.in_scope_sales === 0

  const classTooltip = t('overview.classTooltip')

  if (isEmpty) {
    return (
      <div data-testid="empty-state" className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-8 text-center text-gray-500 dark:text-gray-400">
        {t('overview.empty')}
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <BruktbilfinansieringInfoBox bankName={bankName} filters={filters} tab="oversikt" />
      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <div data-testid="kpi-agent_count" className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('overview.kpi.agentCount')}</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">
            {kpis ? formatCount(kpis.agent_count) : '—'}
          </p>
        </div>
        <div data-testid="kpi-in_scope_sales" title={classTooltip} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('overview.kpi.inScopeSales')}</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">
            {kpis ? formatCount(kpis.in_scope_sales) : '—'}
          </p>
        </div>
        <div data-testid="kpi-financed_sales" title={classTooltip} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('shared.loans')}</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">
            {kpis ? formatCount(kpis.financed_sales) : '—'}
          </p>
        </div>
        <div data-testid="kpi-loan_share" title={classTooltip} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('shared.loanShare')}</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">
            {kpis && kpis.in_scope_sales > 0 ? formatShare(kpis.financed_sales / kpis.in_scope_sales) : '—'}
          </p>
        </div>
        <div data-testid="kpi-bank_financed_sales" title={classTooltip} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('shared.bankLoans')}</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">
            {kpis ? formatCount(kpis.bank_financed_sales) : '—'}
          </p>
        </div>
        <div data-testid="kpi-bank_market_share" title={classTooltip} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('overview.kpi.bankMarketShare')}</p>
          <p className="text-2xl font-bold text-primary-600 dark:text-primary-400">
            {kpis ? formatShare(kpis.bank_market_share) : '—'}
          </p>
        </div>
      </div>

      {/* Bank total NOK */}
      {kpis && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('overview.kpi.bankTotalNok')}</p>
          <p className="text-xl font-bold text-gray-900 dark:text-white">{formatNok(kpis.bank_total_nok)}</p>
        </div>
      )}

      {/* Financing share breakdown — donut pie */}
      {pieSlices.length > 0 && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('overview.pieTitle')}
            </h3>
            <div className="flex gap-1">
              <button onClick={() => setPieMode('antall')}
                className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                  pieMode === 'antall' ? 'bg-primary-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                }`}>{t('shared.count')}</button>
              <button onClick={() => setPieMode('volum')}
                className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                  pieMode === 'volum' ? 'bg-primary-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                }`}>{t('overview.pieVolume')}</button>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={pieData} dataKey="value" nameKey="name"
                  cx="50%" cy="50%" innerRadius={60} outerRadius={120}
                  label={({ name, value }) => pieTotal > 0 ? `${name.split(' ')[0]} ${formatShare(value / pieTotal)}` : name}
                  labelLine={false}>
                  {pieData.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(v: number) => pieMode === 'volum' ? formatNok(v) : formatCount(v)} />
              </PieChart>
            </ResponsiveContainer>
            <div className="space-y-2 self-center">
              {pieData.map((d, i) => (
                <div key={i} className="flex items-center gap-3 text-sm">
                  <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                  <span className="flex-1 text-gray-700 dark:text-gray-300 truncate">{d.name}</span>
                  <span className="text-gray-500 dark:text-gray-400 tabular-nums shrink-0">
                    {pieTotal > 0 ? formatShare(d.value / pieTotal) : '—'}
                  </span>
                  <span className="text-gray-400 dark:text-gray-500 tabular-nums shrink-0">
                    {pieMode === 'volum' ? formatNok(d.value) : formatCount(d.value)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Weekly trend */}
      {trend.length > 0 && (
        <UkentligTrendChart
          data={trend}
          bankName={bankName}
          loanDataCompleteThrough={data?.loan_data_complete_through}
        />
      )}
    </div>
  )
}
